import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { format, parseISO, formatDistanceToNow, isToday, isFuture, isThisWeek } from "date-fns";
import {
  Loader2,
  LogOut,
  Calendar as CalIcon,
  Users,
  FileCheck,
  MessageSquare,
  Inbox,
  ArrowRight,
  Video,
  CheckCircle2,
  RefreshCw,
  Plus,
  Send,
  Target,
  Zap,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { hasAdminAccess } from "@/lib/admin-access";
import { useServerFn } from "@tanstack/react-start";

import { syncPaymentsFromSheet } from "@/lib/payments-sync.functions";
import { getAdminSyncState, recordAdminSync } from "@/lib/admin-sync-state.functions";
import { AdminRefusalStrategies } from "@/components/admin/AdminRefusalStrategies";
import { TestPaymentEmailCard } from "@/components/admin/TestPaymentEmailCard";
import { AdminShell } from "@/components/admin/AdminShell";
import { DashboardSkeleton } from "@/components/admin/Skeletons";
import { EverydayInterviewTracker } from "@/components/admin/EverydayInterviewTracker";
import { IntakeCohortPanel } from "@/components/admin/IntakeCohortPanel";
import { RecentPaymentsWidget } from "@/components/admin/RecentPaymentsWidget";

import { CommunityQueriesWidget } from "@/components/admin/CommunityQueriesWidget";
import { listPaidStudentsLite as listPaidStudents, type PaidStudent } from "@/lib/admin-students.functions";

const MOCK_COUNT: Record<string, number> = {
  basic: 2,
  "basic support": 2,
  full: 4,
  "full support": 4,
  fresher: 4,
  refusal: 5,
  "refusal support": 5,
};

function planMockCount(name: string | null | undefined): number {
  if (!name) return 0;
  const k = name.toLowerCase().trim();
  for (const key of Object.keys(MOCK_COUNT)) if (k.includes(key)) return MOCK_COUNT[key];
  return 0;
}

export const Route = createFileRoute("/admin/")({
  head: () => ({
    meta: [{ title: "Admin Console - WeLivdIt" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: AdminHomePage,
});

type Stats = {
  todaySessions: number;
  upcomingSessions: number;
  weekSessions: number;
  paidStudents: number;
  pendingLeads: number;
  newSignups: number;
  totalMocksToDeliver: number;
  mocksDelivered: number;
  revenueInr: number;
  pendingApprovals: number;
  visaApprovals30d: number;
  visaApprovalsPrev30d: number;
  visaApprovalsTotal: number;
  successRate: number;
  approvedThisMonth: number;
  ds160Pending: number;
  interviews48h: number;
  unansweredQueries: number;
};

type NextSession = {
  id: string;
  name: string;
  plan: string;
  date: string;
  phone: string;
  email: string | null;
  eventName: string;
  mockCount: number;
  source: "payment" | "calendly";
  joinUrl: string | null;
};

export function AdminHomePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);
  const [paidStudentsList, setPaidStudentsList] = useState<PaidStudent[]>([]);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [, tick] = useState(0);
  const autoSyncedRef = useRef(false);

  const listPaid = useServerFn(listPaidStudents);
  const runSync = useServerFn(syncPaymentsFromSheet);
  const getSync = useServerFn(getAdminSyncState);
  const recordSync = useServerFn(recordAdminSync);

  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    const now = new Date();
    const sincePrev60 = new Date(now.getTime() - 60 * 24 * 3600 * 1000).toISOString();
    const [
      { data: pays },
      { data: bookings },
      { data: leads },
      { data: signups },
      { data: milestones },
      { data: approvedRecent },
      { data: approvedVisaRows },
      { data: approvedPayments },
      { data: questions },
      { data: questionReplies },
    ] = await Promise.all([
      supabase
        .from("payments")
        .select(
          "id, user_id, buyer_name, buyer_phone, buyer_email, plan_name, status, interview_date, interview_status, amount_paise, razorpay_order_id, created_at",
        ),
      supabase
        .from("bookings")
        .select("id, invitee_name, invitee_phone, invitee_email, event_name, start_time, status, join_url"),
      supabase.from("enrollment_leads").select("id, status"),
      supabase.from("group_signups").select("id, status"),
      supabase
        .from("admin_student_milestones")
        .select("mocks_requested, mocks_done, ds160_review_requested, ds160_review_done"),
      supabase
        .from("visa_approvals")
        .select("approved_at, student_email")
        .eq("status", "approved")
        .gte("approved_at", sincePrev60),
      supabase.from("visa_approvals").select("id, student_email").eq("status", "approved"),
      supabase
        .from("payments")
        .select("id, buyer_email, buyer_name")
        .eq("status", "paid")
        .eq("interview_status", "approved"),
      supabase.from("guide_questions").select("id, resolved_at").limit(500),
      supabase.from("guide_question_replies").select("question_id, is_admin").limit(2000),
    ]);

    const paid = (pays ?? []).filter((p) => p.status === "paid");

    const groups = new Map<string, typeof paid>();
    for (const p of paid) {
      const email = (p.buyer_email ?? "").toLowerCase().trim();
      const digits = (p.buyer_phone ?? "").replace(/\D/g, "").slice(-10);
      const name = (p.buyer_name ?? "").toLowerCase().trim().replace(/\s+/g, " ");
      const key = email ? `e:${email}` : digits ? `p:${digits}` : name ? `n:${name}` : `id:${p.id}`;
      const arr = groups.get(key);
      if (arr) arr.push(p);
      else groups.set(key, [p]);
    }
    const uniqueKeys = new Set(groups.keys());

    let dedupedRevenuePaise = 0;
    for (const [, members] of groups) {
      const sorted = [...members].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      const isManual = (id: string | null | undefined) =>
        !id || id.startsWith("manual_") || id.startsWith("manual_intake_");
      const master = sorted.find((m) => !isManual(m.razorpay_order_id)) ?? sorted[0];
      dedupedRevenuePaise += master.amount_paise || 0;
    }

    const planByPhone = new Map<string, string>();
    paid.forEach((p) => {
      const d = (p.buyer_phone || "").replace(/\D/g, "");
      if (d) planByPhone.set(d, p.plan_name);
    });

    const fromPay: NextSession[] = paid
      .filter((p) => p.interview_date)
      .map((p) => ({
        id: p.id,
        name: p.buyer_name,
        plan: p.plan_name,
        date: p.interview_date as string,
        phone: p.buyer_phone,
        email: p.buyer_email ?? null,
        eventName: `${p.plan_name} - Visa interview date`,
        mockCount: planMockCount(p.plan_name),
        source: "payment" as const,
        joinUrl: null,
      }));

    const fromBk: NextSession[] = (bookings ?? [])
      .filter((b) => b.status === "active")
      .map((b) => {
        const d = (b.invitee_phone || "").replace(/\D/g, "");
        const plan = (d && planByPhone.get(d)) || b.event_name;
        return {
          id: b.id,
          name: b.invitee_name,
          plan,
          date: b.start_time,
          phone: b.invitee_phone || "",
          email: b.invitee_email ?? null,
          eventName: b.event_name,
          mockCount: planMockCount(plan),
          source: "calendly" as const,
          joinUrl: b.join_url ?? null,
        };
      });

    const all = [...fromPay, ...fromBk]
      .filter((s) => isToday(parseISO(s.date)) || isFuture(parseISO(s.date)))
      .sort((a, b) => a.date.localeCompare(b.date));

    const pendingApprovals = (milestones ?? []).reduce((sum, m) => {
      return sum + Math.max(0, (m.mocks_requested ?? 0) - (m.mocks_done ?? 0));
    }, 0);

    const since30Date = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
    const sincePrev60Date = new Date(now.getTime() - 60 * 24 * 3600 * 1000);
    let visaApprovals30d = 0;
    let visaApprovalsPrev30d = 0;
    for (const r of approvedRecent ?? []) {
      if (!r.approved_at) continue;
      const d = new Date(r.approved_at);
      if (d >= since30Date) visaApprovals30d++;
      else if (d >= sincePrev60Date) visaApprovalsPrev30d++;
    }

    // Deduplicate total approved across visa_approvals and payments tables
    const allApprovedSet = new Set<string>();
    (approvedVisaRows ?? []).forEach((v: any) => {
      const k = (v.student_email || v.id).toLowerCase().trim();
      if (k) allApprovedSet.add(k);
    });
    (approvedPayments ?? []).forEach((p: any) => {
      const k = (p.buyer_email || p.id).toLowerCase().trim();
      if (k) allApprovedSet.add(k);
    });

    const totalApprovedCount = allApprovedSet.size;

    // --- Operational metrics -------------------------------------------------
    // Visa success rate = approved / (approved + refused) across decided interviews.
    const decided = paid.filter((p) =>
      ["approved", "refused"].includes(String((p as { interview_status?: string }).interview_status ?? "")),
    );
    const approvedDecided = decided.filter(
      (p) => String((p as { interview_status?: string }).interview_status) === "approved",
    ).length;
    const successRate = decided.length ? (approvedDecided / decided.length) * 100 : 0;

    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const approvedThisMonth = (approvedRecent ?? []).filter(
      (r) => r.approved_at && new Date(r.approved_at) >= monthStart,
    ).length;

    const ds160Pending = (milestones ?? []).filter(
      (m: { ds160_review_requested?: boolean | null; ds160_review_done?: boolean | null }) =>
        Boolean(m.ds160_review_requested) && !m.ds160_review_done,
    ).length;

    const in48h = new Date(now.getTime() + 48 * 3600 * 1000);
    const interviews48h = paid.filter((p) => {
      if (!p.interview_date) return false;
      const d = new Date(`${String(p.interview_date).slice(0, 10)}T12:00:00`);
      return !isNaN(d.getTime()) && d >= new Date(now.getFullYear(), now.getMonth(), now.getDate()) && d <= in48h;
    }).length;

    const adminAnswered = new Set(
      (questionReplies ?? []).filter((r: { is_admin?: boolean }) => r.is_admin).map((r) => r.question_id),
    );
    const unansweredQueries = (questions ?? []).filter(
      (q: { id: string; resolved_at?: string | null }) => !q.resolved_at && !adminAnswered.has(q.id),
    ).length;

    setStats({
      todaySessions: all.filter((s) => isToday(parseISO(s.date))).length,
      upcomingSessions: all.length,
      weekSessions: all.filter((s) => isThisWeek(parseISO(s.date), { weekStartsOn: 1 })).length,
      paidStudents: uniqueKeys.size,
      pendingLeads: (leads ?? []).filter((l) => l.status === "new").length,
      newSignups: (signups ?? []).filter((s) => s.status === "new").length,
      totalMocksToDeliver: all.reduce((sum, s) => sum + s.mockCount, 0),
      mocksDelivered: (milestones ?? []).reduce(
        (sum, m: { mocks_done?: number | null }) => sum + (m.mocks_done ?? 0),
        0,
      ),
      revenueInr: dedupedRevenuePaise / 100,
      pendingApprovals,
      visaApprovals30d: totalApprovedCount,
      visaApprovalsPrev30d,
      visaApprovalsTotal: totalApprovedCount,
      successRate,
      approvedThisMonth,
      ds160Pending,
      interviews48h,
      unansweredQueries,
    });

    // Non-blocking: cohort cards fill in after the dashboard is already visible.
    listPaid()
      .then((res) => setPaidStudentsList(res.students))
      .catch((e) => console.warn("Couldn't load paid students", e));
  }, [listPaid]);

  const handleSync = useCallback(
    async (silent = false) => {
      if (syncing) return;
      setSyncing(true);
      try {
        await runSync();
        const rec = await recordSync();
        setLastSyncedAt(rec.syncedAt);
        await load();
        if (!silent) toast.success("Synced successfully");
      } catch (e) {
        if (!silent) toast.error(e instanceof Error ? e.message : "Sync failed");
        console.error(e);
      } finally {
        setSyncing(false);
      }
    },
    [runSync, recordSync, load, syncing],
  );

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        navigate({ to: "/auth", search: { redirect: "/admin" } });
        return;
      }
      if (!active) return;
      if (!(await hasAdminAccess(sess.session))) {
        setAuthorized(false);
        setLoading(false);
        return;
      }
      setAuthorized(true);
      getSync()
        .then((s) => active && setLastSyncedAt(s.syncedAt))
        .catch(() => {});
      await load();
      setLoading(false);
      // Sheet sync is on-demand only (Sync button) — no auto-run on page open.
      autoSyncedRef.current = true;
    })();
    return () => {
      active = false;
    };
  }, [navigate, load, getSync, handleSync]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  };

  if (loading)
    return (
      <AdminShell title="Admin Console" description="Loading your latest numbers…">
        <DashboardSkeleton />
      </AdminShell>
    );

  if (!authorized)
    return (
      <div className="admin-surface grid min-h-[60vh] place-items-center bg-slate-100 px-5 dark:bg-zinc-950">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Not Authorized</h1>
          <p className="mt-2 text-sm text-slate-500">Your account does not have admin permissions.</p>
          <Button variant="outline" onClick={handleSignOut} className="mt-5 rounded-xl">
            Sign Out
          </Button>
        </div>
      </div>
    );

  const kpis: Array<{
    label: string;
    value: string | number;
    sub: string;
    icon: typeof CalIcon;
    trend: string;
    priority: boolean;
    to: string;
    search?: Record<string, string>;
  }> = [
    {
      label: "Visa Success Rate",
      value: `${(stats?.successRate ?? 0).toFixed(1)}%`,
      sub: `${stats?.approvedThisMonth ?? 0} approved this month · ${stats?.visaApprovalsTotal ?? 0} lifetime wins`,
      icon: CheckCircle2,
      trend: "approval rate",
      priority: false,
      to: "/admin/interview-track",
      search: { status: "approved" },
    },
    {
      label: "DS-160 Reviews Pending",
      value: stats?.ds160Pending ?? 0,
      sub: "live screen-share reviews pending",
      icon: FileCheck,
      trend: (stats?.ds160Pending ?? 0) > 0 ? "action needed" : "clear",
      priority: (stats?.ds160Pending ?? 0) > 0,
      to: "/admin/mock-scheduling",
    },
    {
      label: "Upcoming Interviews (48h)",
      value: stats?.interviews48h ?? 0,
      sub: "high-priority student follow-ups",
      icon: CalIcon,
      trend: (stats?.interviews48h ?? 0) > 0 ? "urgent" : "quiet",
      priority: (stats?.interviews48h ?? 0) > 0,
      to: "/admin/interview-track",
    },
    {
      label: "Unanswered Community Queries",
      value: stats?.unansweredQueries ?? 0,
      sub: "forum questions awaiting reply",
      icon: MessageSquare,
      trend: (stats?.unansweredQueries ?? 0) > 0 ? "reply needed" : "all answered",
      priority: (stats?.unansweredQueries ?? 0) > 0,
      to: "/admin",
    },
  ];

  const priorityRibbon = [
    { key: "inbox", icon: MessageSquare, count: null as number | null, label: "Student Inbox", to: "/admin/inbox" },
    {
      key: "track",
      icon: Target,
      count: stats?.interviews48h ?? 0,
      label: "Interview Track",
      to: "/admin/interview-track",
    },
    {
      key: "approvals",
      icon: CheckCircle2,
      count: stats?.pendingApprovals ?? 0,
      label: "Mocks Awaiting Review",
      to: "/admin/students",
    },
    { key: "leads", icon: Inbox, count: stats?.pendingLeads ?? 0, label: "New Leads", to: "/admin/leads" },
    { key: "signups", icon: Users, count: stats?.newSignups ?? 0, label: "Signups to Triage", to: "/admin/signups" },
    { key: "live", icon: Video, count: null as number | null, label: "Live Broadcast", to: "/admin/live-sessions" },
  ];

  return (
    <AdminShell
      title="Dashboard Overview"
      description={format(new Date(), "EEEE, d MMMM yyyy")}
      actions={
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Action Speed Dial */}
          <Link
            to="/admin/payments"
            search={{ category: "mocks" } as never}
            className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-3.5 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-slate-800 transition dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            <Plus className="h-3.5 w-3.5" /> Log Payment
          </Link>
          <Link
            to="/admin/broadcast"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-bold text-slate-700 shadow-sm hover:bg-slate-50 transition dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            <Send className="h-3.5 w-3.5 text-amber-500" /> Broadcast
          </Link>

          <div className="hidden items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500 shadow-sm sm:flex dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
            <span className={`h-2 w-2 rounded-full ${syncing ? "animate-pulse bg-amber-500" : "bg-emerald-500"}`} />
            {syncing
              ? "Syncing..."
              : lastSyncedAt
                ? `Synced ${formatDistanceToNow(parseISO(lastSyncedAt), { addSuffix: true })}`
                : "Not synced"}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleSync(false)}
            disabled={syncing}
            className="gap-2 rounded-xl shadow-sm"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> Sync
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSignOut}
            className="gap-1.5 rounded-xl text-slate-500 hover:text-slate-900 dark:hover:text-white"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      }
    >
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-4">
        <div className="min-w-0 lg:col-span-3">
          <IntakeCohortPanel students={paidStudentsList} />
        </div>
        <div className="min-w-0 lg:col-span-1">
          <RecentPaymentsWidget compact />
        </div>
      </div>

      {/* Section 1 - Everyday Interview Tracker (highest priority) */}
      <div className="mt-6">
        <EverydayInterviewTracker onChange={load} />
      </div>

      {/* Section 2 - KPI Cards Grid */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <Link
            key={k.label}
            to={k.to}
            search={(k.search ?? undefined) as never}
            className={`group relative block rounded-2xl border p-4.5 transition-all duration-200 ${
              k.priority
                ? "border-amber-300 bg-amber-50/60 shadow-sm hover:shadow-md dark:border-amber-500/30 dark:bg-amber-950/20"
                : "border-slate-200/80 bg-white shadow-sm hover:border-slate-300 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900"
            }`}
          >
            {k.priority && (
              <span className="absolute right-3.5 top-3.5 flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
              </span>
            )}
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-400">
                <k.icon className="h-4 w-4 text-amber-600 dark:text-amber-400" /> {k.label}
              </span>
              <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500 dark:bg-zinc-800 dark:text-zinc-400">
                {k.trend}
              </span>
            </div>
            <p className="mt-3 text-3xl font-black tracking-tight text-slate-900 tabular-nums dark:text-white">
              {k.value}
            </p>
            <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-slate-500 dark:text-zinc-400">
              {k.sub}
              <ArrowRight className="h-3 w-3 opacity-0 transition group-hover:opacity-60" />
            </p>
          </Link>
        ))}
      </div>

      {/* Modern Focus Bar */}
      <div className="mt-6 rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
        <div className="no-scrollbar flex items-center gap-2 overflow-x-auto">
          <span className="shrink-0 inline-flex items-center gap-1.5 px-2 text-[11px] font-black uppercase tracking-wider text-slate-400">
            <Zap className="h-3.5 w-3.5 text-amber-500" /> Priority Focus
          </span>
          {priorityRibbon.map((i) => {
            const Icon = i.icon;
            const isPriority = (i.count ?? 0) > 0;
            return (
              <Link
                key={i.key}
                to={i.to}
                className={`shrink-0 inline-flex items-center gap-2 rounded-xl border px-3.5 py-1.5 text-xs font-bold transition ${
                  isPriority
                    ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100"
                    : "border-slate-200 bg-slate-50/60 text-slate-700 hover:border-slate-300 hover:bg-slate-100 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-300 dark:hover:bg-zinc-800"
                }`}
              >
                <Icon className="h-3.5 w-3.5 text-slate-400" />
                <span>{i.label}</span>
                {i.count !== null && i.count > 0 && (
                  <span className="inline-flex items-center rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-black text-white tabular-nums">
                    {i.count}
                  </span>
                )}
                <ArrowRight className="h-3 w-3 opacity-40" />
              </Link>
            );
          })}
        </div>
      </div>

      {/* Main Grid Workspace */}
      <div className="mt-6 grid gap-6 lg:grid-cols-10">
        {/* Left Column (70%) */}
        <div className="min-w-0 space-y-6 lg:col-span-7">
          <AdminRefusalStrategies />
          <CommunityQueriesWidget />
        </div>

        {/* Right Column (30%) */}
        <div className="min-w-0 space-y-6 lg:col-span-3">
          {/* Operations Target Tracker Widget */}
          <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3 dark:border-zinc-800">
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-900 dark:text-white">
                <Target className="h-4 w-4 text-amber-500" /> Operations Goal
              </div>
              <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                On Track
              </span>
            </div>

            <div className="mt-4 space-y-4">
              <div>
                <div className="flex justify-between text-xs font-bold text-slate-700 dark:text-zinc-300">
                  <span>Mocks Delivered</span>
                  <span>
                    {stats?.mocksDelivered ?? 0} / {(stats?.mocksDelivered ?? 0) + (stats?.totalMocksToDeliver ?? 0)}
                  </span>
                </div>
                <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-zinc-800">
                  <div
                    className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
                    style={{
                      width: `${Math.min(
                        100,
                        ((stats?.mocksDelivered ?? 0) /
                          Math.max(1, (stats?.mocksDelivered ?? 0) + (stats?.totalMocksToDeliver ?? 0))) *
                          100,
                      ).toFixed(0)}%`,
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs font-bold text-slate-700 dark:text-zinc-300">
                  <span>Visa Approval Rate</span>
                  <span>{(stats?.successRate ?? 0).toFixed(1)}%</span>
                </div>
                <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-zinc-800">
                  <div
                    className="h-full bg-emerald-500"
                    style={{ width: `${Math.min(100, stats?.successRate ?? 0).toFixed(1)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Collapsible Developer Tools */}
      <details className="group mt-8 overflow-hidden rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 transition dark:border-zinc-800 dark:bg-zinc-950/40">
        <summary className="flex cursor-pointer select-none items-center justify-between px-4 py-3 text-xs font-black uppercase tracking-wider text-slate-500 hover:text-slate-900 dark:hover:text-white">
          <span>Developer Tools & Mailer Test</span>
          <span className="text-[10px] font-bold text-slate-400 group-open:hidden">Click to expand</span>
        </summary>
        <div className="border-t border-slate-200/60 p-4 pt-2 dark:border-zinc-800">
          <TestPaymentEmailCard />
        </div>
      </details>

      <span className="sr-only" aria-hidden>
        {paidStudentsList.length}
      </span>
    </AdminShell>
  );
}
